import { useState, useCallback, useRef, useEffect } from "react";
import { TranslationService } from "../common/translation/translation-service";
import { useLanguage } from "../contexts/LanguageContext";
import { GameInfo } from "src/_genApi/scs";

export interface TranslationState {
  isTranslating: boolean;
  error: string | null;
  progress: number;
}

export interface TranslationResult {
  success: boolean;
  error?: string;
}

/**
 * Segmented ceilings (%). Progress asymptotically approaches the ceiling of the
 * current stage, then jumps to the next stage after a wall-clock threshold.
 * The final jump to 100 happens only when the API resolves.
 */
const STAGES = [
  { ceiling: 22, dwellMs: 3000 },   // analyze
  { ceiling: 68, dwellMs: 18000 },  // generate (longest)
  { ceiling: 92, dwellMs: 6000 },   // pack
  { ceiling: 97, dwellMs: Infinity }, // finalize — holds until resolve
];

const TICK_MS = 220;
const DECAY = 0.07; // higher = faster approach to ceiling

export const useGameTranslation = () => {
  const { language } = useLanguage();
  const [translationState, setTranslationState] = useState<TranslationState>({
    isTranslating: false,
    error: null,
    progress: 0,
  });

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rafRef = useRef<number | null>(null);
  const stageStartRef = useRef<number>(0);
  const stageIndexRef = useRef<number>(0);
  // Read without subscribing — so the finish animation can read the latest value
  // without forcing a ticker restart.
  const progressRef = useRef<number>(0);

  const clearTicker = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearTicker();
  }, [clearTicker]);

  /**
   * Catch-up animation on completion. Smoothly ramps progress from its current
   * value to 100 using ease-out cubic over a duration proportional to the
   * remaining distance — so fast-finishing tasks don't snap 15% → 100%.
   */
  const runFinishAnimation = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      const start = progressRef.current;
      const distance = 100 - start;
      if (distance <= 0.5) {
        setTranslationState((s) => ({ ...s, progress: 100 }));
        progressRef.current = 100;
        resolve();
        return;
      }
      // Duration scales with remaining distance: short distance → snappy (~180 ms),
      // long distance (fast completion) → longer sweep (~520 ms) so it feels earned.
      const duration = Math.max(180, Math.min(520, distance * 6));
      const startedAt = performance.now();

      const step = (now: number) => {
        const elapsed = now - startedAt;
        const linear = Math.min(1, elapsed / duration);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - linear, 3);
        const next = start + distance * eased;
        progressRef.current = next;
        setTranslationState((s) => ({ ...s, progress: next }));

        if (linear < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          rafRef.current = null;
          resolve();
        }
      };
      rafRef.current = requestAnimationFrame(step);
    });
  }, []);

  const needsTranslation = useCallback(
    (gameInfo: GameInfo): boolean => {
      if (!gameInfo.originLanguage) return false;
      return gameInfo.originLanguage !== language;
    },
    [language],
  );

  const startProgressTicker = useCallback(() => {
    clearTicker();
    stageIndexRef.current = 0;
    stageStartRef.current = Date.now();
    progressRef.current = 0;
    setTranslationState((s) => ({ ...s, progress: 0 }));

    intervalRef.current = setInterval(() => {
      setTranslationState((prev) => {
        if (!prev.isTranslating) return prev;

        const stage = STAGES[stageIndexRef.current];
        if (!stage) return prev;

        const elapsed = Date.now() - stageStartRef.current;
        if (
          elapsed >= stage.dwellMs &&
          stageIndexRef.current < STAGES.length - 1
        ) {
          stageIndexRef.current += 1;
          stageStartRef.current = Date.now();
        }

        const currentStage = STAGES[stageIndexRef.current];
        const next =
          prev.progress + (currentStage.ceiling - prev.progress) * DECAY;
        const clamped = Math.min(next, currentStage.ceiling);
        progressRef.current = clamped;
        return { ...prev, progress: clamped };
      });
    }, TICK_MS);
  }, [clearTicker]);

  const translateGame = useCallback(
    async (gameInfo: GameInfo): Promise<TranslationResult> => {
      progressRef.current = 0;
      setTranslationState({ isTranslating: true, error: null, progress: 0 });
      startProgressTicker();

      try {
        const translationService = new TranslationService();
        await translationService.translateStory(gameInfo.id, language);

        // Smoothly ramp from wherever we are to 100 — no more 15% → 100 jump.
        await runFinishAnimation();
        setTranslationState({ isTranslating: false, error: null, progress: 100 });

        // Hold the 100% state briefly so the user sees the completion.
        setTimeout(() => {
          progressRef.current = 0;
          setTranslationState({ isTranslating: false, error: null, progress: 0 });
        }, 700);

        return { success: true };
      } catch (error) {
        clearTicker();
        console.error("Translation error:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Translation failed";

        progressRef.current = 0;
        setTranslationState({
          isTranslating: false,
          error: errorMessage,
          progress: 0,
        });
        return { success: false, error: errorMessage };
      }
    },
    [language, startProgressTicker, runFinishAnimation, clearTicker],
  );

  const clearTranslationState = useCallback(() => {
    clearTicker();
    progressRef.current = 0;
    setTranslationState({ isTranslating: false, error: null, progress: 0 });
  }, [clearTicker]);

  return {
    translationState,
    needsTranslation,
    translateGame,
    clearTranslationState,
  };
};
